import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, X, Plus, Save, Download, RotateCcw, Trash2, Copy, Check } from "lucide-react";
import { api, API_BASE, type ApiResponse } from "@/api/client";
import type { MediaResponse } from "@ckgo2/shared/types";
import { useMedia } from "@/hooks/useMedia";
import { blurStyle } from "@/lib/mediaStyle";
import { formatFileSize } from "@/lib/mediaFormat";
import FocalPointEditor from "@/components/media/FocalPointEditor";
import { type AlbumFolder } from "@/components/media/AlbumTree";

interface UsageResponse {
  destinations: Array<{ id: string; slug: string; name: string }>;
  trips: Array<{ id: string; slug: string; title: string }>;
  blogPosts: Array<{ id: string; slug: string; title: string }>;
  activities: Array<{ id: string; slug: string; title: string }>;
  guides: Array<{ id: string; slug: string; name: string }>;
}

/** Right-side drawer to edit a single media row + its usage, albums, duplicates. */
export default function MediaDetailDrawer({
  id,
  inTrash,
  onClose,
  onChanged,
}: {
  id: string;
  inTrash: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useMedia(id);
  const media = data?.data;

  const [alt, setAlt] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [focal, setFocal] = useState({ focalPointX: 0.5, focalPointY: 0.5 });
  const [copied, setCopied] = useState(false);

  // Sync local state when media loads
  useEffect(() => {
    if (media) {
      setAlt(media.alt ?? "");
      setTitle(media.title ?? "");
      setDescription(media.description ?? "");
      setTags(media.tags ?? []);
      setFocal({ focalPointX: media.focalPointX, focalPointY: media.focalPointY });
    }
  }, [media]);

  const { data: usageData } = useQuery({
    queryKey: ["media-usage", id],
    queryFn: () => api.get<ApiResponse<UsageResponse>>(`/api/admin/upload/${id}/usage`),
  });
  const usage = usageData?.data;

  const { data: dupData } = useQuery({
    queryKey: ["media-duplicates", id],
    queryFn: () =>
      api.get<ApiResponse<{ similar: MediaResponse[] }>>(`/api/admin/upload/${id}/duplicates`),
  });
  const duplicates = dupData?.data?.similar ?? [];

  // Album membership for this image (the "In albums" section).
  const { data: memberData } = useQuery({
    queryKey: ["media-album-membership", id],
    queryFn: () =>
      api.get<ApiResponse<{ id: string; name: string }[]>>(
        `/api/admin/media-folders/for-media/${id}`,
      ),
  });
  const memberAlbums = memberData?.data ?? [];
  const { data: allAlbumsData } = useQuery({
    queryKey: ["media-folders"],
    queryFn: () => api.get<ApiResponse<AlbumFolder[]>>("/api/admin/media-folders"),
  });
  const allAlbums = allAlbumsData?.data ?? [];
  const refreshAlbums = () => {
    queryClient.invalidateQueries({ queryKey: ["media-album-membership", id] });
    queryClient.invalidateQueries({ queryKey: ["media-folders"] });
  };
  const addAlbumMutation = useMutation({
    mutationFn: (folderId: string) =>
      api.post(`/api/admin/media-folders/${folderId}/items`, { mediaIds: [id] }),
    onSuccess: refreshAlbums,
  });
  const removeAlbumMutation = useMutation({
    mutationFn: (folderId: string) =>
      api.delete(`/api/admin/media-folders/${folderId}/items`, { mediaIds: [id] }),
    onSuccess: refreshAlbums,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api.patch<ApiResponse<MediaResponse>>(`/api/admin/upload/${id}`, {
        alt: alt.trim(),
        title: title.trim() || undefined,
        description: description.trim() || null,
        tags,
        focalPointX: focal.focalPointX,
        focalPointY: focal.focalPointY,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", id] });
      onChanged();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (force: boolean) =>
      api.delete(`/api/admin/upload/${id}${force ? "?force=true" : ""}`),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => api.post(`/api/admin/upload/${id}/restore`, {}),
    onSuccess: () => {
      onChanged();
      queryClient.invalidateQueries({ queryKey: ["media", id] });
    },
  });

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const usageLinks: Array<{ to: string; label: string }> = useMemo(() => {
    if (!usage) return [];
    return [
      ...usage.destinations.map((d) => ({
        to: `/destinations/${d.id}`,
        label: `Destination: ${d.name}`,
      })),
      ...usage.trips.map((t) => ({ to: `/trips/${t.id}`, label: `Trip: ${t.title}` })),
      ...usage.blogPosts.map((b) => ({ to: `/blog/${b.id}`, label: `Blog: ${b.title}` })),
      ...usage.activities.map((a) => ({
        to: `/inspirations/${a.id}`,
        label: `Inspiration: ${a.title}`,
      })),
      ...usage.guides.map((g) => ({ to: `/guides/${g.id}`, label: `Guide: ${g.name}` })),
    ];
  }, [usage]);

  const altRequiredError = alt.trim().length === 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-2xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Media details</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading || !media ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="flex-1 space-y-5 p-4">
            {/* Preview with LQIP */}
            <div className="overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
              <img
                src={media.url}
                alt={media.alt}
                className="max-h-64 w-full object-contain"
                style={blurStyle(media.blurDataUrl)}
              />
            </div>

            {/* Alt + title */}
            <div>
              <label className="label">Alt text (required)</label>
              <input
                value={alt}
                maxLength={125}
                onChange={(e) => setAlt(e.target.value)}
                className={`input ${altRequiredError ? "input-error" : ""}`}
                placeholder="Describe the image for SEO + accessibility"
              />
              {altRequiredError && (
                <p className="text-error-600 mt-1 text-xs">Alt text is required.</p>
              )}
            </div>
            <div>
              <label className="label">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input"
                placeholder="Optional title"
              />
            </div>

            {/* Description (longer, for keyword search) */}
            <div>
              <label className="label">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={500}
                className="input"
                placeholder="Longer description — improves keyword search"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="label">Tags</label>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                  >
                    {t}
                    <button type="button" onClick={() => setTags((p) => p.filter((x) => x !== t))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="input flex-1"
                  placeholder="Add tag and press Enter"
                />
                <button type="button" onClick={addTag} className="btn-ghost text-sm">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Focal point */}
            <div>
              <label className="label">Focal point</label>
              <FocalPointEditor
                src={media.url}
                alt={media.alt}
                focalPointX={focal.focalPointX}
                focalPointY={focal.focalPointY}
                onChange={setFocal}
              />
            </div>

            {/* Public URL + copy */}
            <div>
              <label className="label">Public URL</label>
              <div className="flex gap-2">
                <input readOnly value={media.url} className="input flex-1 text-xs" />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(media.url).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    });
                  }}
                  className="btn-ghost text-sm"
                  aria-label="Copy URL"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Metadata */}
            <dl className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div>
                <dt className="font-medium">Dimensions</dt>
                <dd>
                  {media.width}×{media.height}
                </dd>
              </div>
              <div>
                <dt className="font-medium">Size</dt>
                <dd>{formatFileSize(media.sizeBytes)}</dd>
              </div>
              <div>
                <dt className="font-medium">Format</dt>
                <dd>{media.mimeType}</dd>
              </div>
              <div>
                <dt className="font-medium">Uploaded</dt>
                <dd>{new Date(media.uploadedAt).toLocaleDateString()}</dd>
              </div>
              {media.uploadedBy && (
                <div className="col-span-2">
                  <dt className="font-medium">Uploaded by</dt>
                  <dd>{media.uploadedBy}</dd>
                </div>
              )}
            </dl>

            {/* Used in */}
            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Used in</h3>
              {usageLinks.length === 0 ? (
                <p className="text-xs text-gray-500">Not referenced by any entity.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {usageLinks.map((u) => (
                    <li key={u.to}>
                      <Link
                        to={u.to}
                        className="text-primary-600 hover:underline"
                        onClick={onClose}
                      >
                        {u.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* In albums */}
            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                In albums
              </h3>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {memberAlbums.length === 0 ? (
                  <p className="text-xs text-gray-500">Not in any album.</p>
                ) : (
                  memberAlbums.map((a) => (
                    <span
                      key={a.id}
                      className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                    >
                      {a.name}
                      <button
                        type="button"
                        onClick={() => removeAlbumMutation.mutate(a.id)}
                        aria-label={`Remove from ${a.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))
                )}
              </div>
              <select
                value=""
                onChange={(e) => e.target.value && addAlbumMutation.mutate(e.target.value)}
                className="input text-sm"
              >
                <option value="">Add to album…</option>
                {allAlbums
                  .filter((a) => !memberAlbums.some((m) => m.id === a.id))
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Exact duplicates */}
            {duplicates.length > 0 && (
              <div>
                <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                  Exact duplicates
                </h3>
                <div className="flex flex-wrap gap-2">
                  {duplicates.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      title={d.alt}
                      className="h-14 w-14 overflow-hidden rounded border border-gray-200 dark:border-gray-700"
                    >
                      <img src={d.thumbUrl} alt={d.alt} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions footer */}
        {media && (
          <div className="flex flex-wrap gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
            {!inTrash && (
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || altRequiredError}
                className="btn-primary text-sm"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saveMutation.isSuccess ? "Saved" : "Save"}
              </button>
            )}
            {media.r2KeyOriginal && (
              <a
                href={`${API_BASE}/api/media/${media.r2KeyOriginal}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-sm"
              >
                <Download className="h-4 w-4" /> Original
              </a>
            )}
            {inTrash ? (
              <button
                onClick={() => restoreMutation.mutate()}
                disabled={restoreMutation.isPending}
                className="btn-ghost text-sm"
              >
                <RotateCcw className="h-4 w-4" /> Restore
              </button>
            ) : (
              <button
                onClick={() => {
                  if (confirm("Move this image to trash?")) deleteMutation.mutate(false);
                }}
                className="btn-ghost text-error-600 hover:bg-error-50 text-sm"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
            <button
              onClick={() => {
                if (confirm("Permanently delete this image? This cannot be undone."))
                  deleteMutation.mutate(true);
              }}
              className="btn-ghost text-error-600 hover:bg-error-50 ml-auto text-sm"
            >
              <Trash2 className="h-4 w-4" /> Hard delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
